import { useEffect, useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Clock, Users, Calendar, Pencil } from "lucide-react";
import ModePicker from "@/components/ModePicker";
import AddressAutocomplete from "@/components/AddressAutocomplete";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function EditRide() {
  const { id } = useParams();
  const nav = useNavigate();
  const TODAY = todayISO();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ride, setRide] = useState(null);
  const [date, setDate] = useState(TODAY);
  const [time, setTime] = useState("08:00");
  const [destination, setDestination] = useState("");
  const [destinationAddress, setDestinationAddress] = useState("");
  const [origin, setOrigin] = useState("");
  const [originAddress, setOriginAddress] = useState("");
  const [seats, setSeats] = useState(3);
  const [notes, setNotes] = useState("");
  const [mode, setMode] = useState("car");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/rides/${id}`);
        setRide(data);
        setDate(data.date);
        setTime(data.time);
        setDestination(data.destination);
        setDestinationAddress(data.destination_address || "");
        setOrigin(data.origin || "");
        setOriginAddress(data.origin_address || "");
        setSeats(data.seats || 3);
        setNotes(data.notes || "");
        setMode(data.mode || "car");
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const passengerCount = ride ? ride.passengers.length : 0;
  const isRequest = ride && ride.type === "request" && !ride.driver_id;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (date < TODAY) {
      toast.error("La data non può essere nel passato");
      return;
    }
    if (!isRequest && seats < passengerCount) {
      toast.error(`I posti non possono essere inferiori ai passeggeri attuali (${passengerCount})`);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/rides/${id}`, {
        date, time, destination,
        destination_address: destinationAddress || null,
        origin: origin || null,
        origin_address: originAddress || null,
        seats: isRequest ? 0 : Number(seats),
        notes: notes || null,
        mode,
      });
      toast.success(isRequest ? "Richiesta aggiornata!" : "Corsa aggiornata!");
      nav(`/rides/${id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="brut-card p-8 text-center font-bold max-w-2xl mx-auto">Caricamento…</div>;
  if (error) {
    return (
      <div className="brut-card p-8 text-center max-w-2xl mx-auto">
        <p className="font-heading text-xl font-bold mb-2">Impossibile caricare</p>
        <p className="text-[#8A91A8] mb-4">{error}</p>
        <Link to="/dashboard" className="brut-btn">Torna alle corse</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto anim-slide-up">
      <Link to={`/rides/${id}`} className="inline-flex items-center gap-2 mb-4 font-bold text-sm text-[#1A2A6C] hover:underline" data-testid="editride-back">
        <ArrowLeft className="w-4 h-4" /> Annulla
      </Link>

      <div className="brut-card p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 grid place-items-center rounded-2xl bg-[#1A2A6C] text-white shadow-[0_6px_18px_rgba(26,42,108,0.32)]">
            <Pencil className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{isRequest ? "Modifica richiesta" : "Modifica corsa"}</h1>
            <p className="text-sm text-[#8A91A8]">Aggiorna i dettagli della corsa.</p>
          </div>
        </div>

        {passengerCount > 0 && (
          <div className="rounded-2xl bg-[#FFF7E0] border border-[#FFE9A8] p-3 mb-5 text-sm">
            <p className="font-semibold text-[#1A1D29]">Attenzione</p>
            <p className="text-[#7A6B2E]">Ci sono già {passengerCount} {passengerCount === 1 ? "passeggero" : "passeggeri"}. Non puoi ridurre i posti sotto questo numero. Se cambi data, ora o destinazione i passeggeri riceveranno una notifica.</p>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="brut-label"><Calendar className="w-3 h-3 inline mr-1" /> Data</label>
              <input type="date" className="brut-input" min={TODAY} value={date} onChange={(e) => setDate(e.target.value)} required data-testid="editride-date-input" />
            </div>
            <div>
              <label className="brut-label"><Clock className="w-3 h-3 inline mr-1" /> Ora</label>
              <input type="time" className="brut-input" value={time} onChange={(e) => setTime(e.target.value)} required data-testid="editride-time-input" />
            </div>
          </div>

          <div>
            <label className="brut-label"><MapPin className="w-3 h-3 inline mr-1" /> Destinazione</label>
            <input type="text" className="brut-input" value={destination} onChange={(e) => setDestination(e.target.value)} required maxLength={200} data-testid="editride-destination-input" />
            <div className="mt-2">
              <AddressAutocomplete value={destinationAddress} onChange={setDestinationAddress} placeholder="Cerca via, indirizzo…" testId="editride-destination-address-input" />
            </div>
          </div>

          <div>
            <label className="brut-label">Modalità di trasporto</label>
            <ModePicker value={mode} onChange={setMode} />
          </div>

          <div>
            <label className="brut-label">Punto di partenza</label>
            <input type="text" className="brut-input" value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="Es. Casa, Ufficio…" maxLength={200} data-testid="editride-origin-input" />
            <div className="mt-2">
              <AddressAutocomplete value={originAddress} onChange={setOriginAddress} placeholder="Cerca via, indirizzo…" testId="editride-origin-address-input" />
            </div>
          </div>

          <div className={(isRequest || mode !== "car") ? "hidden" : ""}>
            <label className="brut-label"><Users className="w-3 h-3 inline mr-1" /> Posti disponibili</label>
            <div className="flex items-center gap-2 flex-wrap">
              {[1, 2, 3, 4, 5, 6, 7].map((n) => {
                const disabled = n < passengerCount;
                return (
                  <button
                    type="button"
                    key={n}
                    onClick={() => !disabled && setSeats(n)}
                    disabled={disabled}
                    className={`w-11 h-11 rounded-2xl font-heading font-bold text-lg transition-all ${
                      seats === n
                        ? "bg-[#1A2A6C] text-white shadow-[0_6px_18px_rgba(26,42,108,0.32)]"
                        : disabled
                        ? "bg-[#F5F6FA] text-[#C9D1E8] cursor-not-allowed"
                        : "bg-white border border-[#ECEEF5] text-[#1A1D29] hover:bg-[#F5F6FA]"
                    }`}
                    data-testid={`editride-seats-${n}`}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
            {passengerCount > 0 && (
              <p className="text-xs text-[#8A91A8] mt-2">Min {passengerCount} (passeggeri attuali).</p>
            )}
          </div>

          <div>
            <label className="brut-label">Note (opzionale)</label>
            <textarea className="brut-input min-h-[90px] resize-none" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} data-testid="editride-notes-input" />
          </div>

          <div className="flex gap-3 pt-2">
            <Link to={`/rides/${id}`} className="brut-btn brut-btn-ghost" data-testid="editride-cancel">Annulla</Link>
            <button type="submit" className="brut-btn flex-1" disabled={saving} data-testid="editride-submit">
              {saving ? "Salvataggio…" : "Salva modifiche"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
